import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { HomeIcon, UtensilsIcon, ShirtIcon, SparklesIcon, ShoppingCartIcon, CarIcon, PillIcon, ScissorsIcon, BedDoubleIcon, BathIcon, HeartHandshakeIcon, CheckCircleIcon, ArrowRightIcon } from 'lucide-react';
const services = [{
  icon: HeartHandshakeIcon,
  title: 'In-Home Companionship',
  desc: 'Our service providers are available to provide clients company and engage in conversation, or to sit down to share a meal together. Social contact such as this is proven to enhance the health, happiness and quality of life of seniors.'
}, {
  icon: UtensilsIcon,
  title: 'Meal Preparation',
  desc: 'Our care providers prepare hot, delicious, and nutritious meals, allowing clients to help with preparation according to their ability. This service helps our clients get the nutrition they need to enjoy healthier living.'
}, {
  icon: ShirtIcon,
  title: 'Laundry',
  desc: "Services can be done in either the client's home or the Laundromat and include washing, drying, ironing and putting things away. We involve our clients to make sure laundry is done according to their preferences."
}, {
  icon: SparklesIcon,
  title: 'Light Housekeeping',
  desc: 'Includes vacuuming, dusting, sweeping and mopping floors, cleaning bathrooms, cleaning kitchens, straightening all rooms, organizing closets and drawers, and cleaning interior windows reachable without a ladder.'
}, {
  icon: ShoppingCartIcon,
  title: 'Grocery Shopping & Errands',
  desc: "Our care providers shop for groceries at stores of their clients' preference and run errands, such as picking up prescriptions or going to the post office. Clients may choose to stay home or help with the shopping as a way to stay active."
}, {
  icon: CarIcon,
  title: 'Incidental Transportation',
  desc: 'We provide transportation incidental to other care services, such as taking clients to medical appointments, barbershops, beauty salons, shopping, or wherever else a client wants to go. Getting seniors out and about is good for their mind, body and spirit.'
}, {
  icon: PillIcon,
  title: 'Medication Reminders',
  desc: 'Our care providers can help ensure that clients take their own medication as prescribed. We assist with opening medication containers, reading labels and reminding the client when it is time to take a dose.'
}, {
  icon: ScissorsIcon,
  title: 'Grooming Guidance',
  desc: 'We assist clients with washing and combing their hair and other grooming tasks to enable them to feel good about their appearance.'
}, {
  icon: BedDoubleIcon,
  title: 'Live-In Services',
  desc: "Edvin Home Healthcare Solutions tailors care to each client's needs — from a few hours a week to full-time care. The client and family may designate the types of non-medical in-home care services that are to be provided."
}, {
  icon: BathIcon,
  title: 'Personal Care Services',
  desc: 'We help clients look and feel their best with bathing, grooming and hygiene services. Bathing not only cleanses the skin, but goes deeper to refresh the spirit, provide relaxation and stimulate circulation.'
}];
export function InHomeCareServicesPage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: servicesRef,
    isVisible: isServicesVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-659">
      <PageBanner title="In-Home Care Services" subtitle="A full range of in-home services tailored to support independent living at home." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'In-Home Care Services',
      path: '/services/in-home-care'
    }]} data-id="element-660" />

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white" data-id="element-661">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-662">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-663">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-664">
              <HomeIcon className="h-4 w-4 text-primary" data-id="element-665" />
              <span className="text-primary text-sm font-semibold" data-id="element-666">
                Comprehensive Home Care
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-667">
              Personalized Care for Independent Living
            </h2>

            <p className="text-lg text-dark-muted leading-relaxed mb-8" data-id="element-668">
              Each Edvin Home Healthcare Solutions client has unique care needs.
              That is why we offer a full range of in-home services — to tailor
              care plans that provide our senior and other adult clients the
              right amount and types of help to support independent living at
              home.
            </p>

            {/* Service categories */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-669">
              {[{
              label: 'Companionship & Care',
              color: 'bg-primary/10 text-primary border-primary/20'
            }, {
              label: 'Personal Care Services',
              color: 'bg-secondary/10 text-secondary border-secondary/20'
            }, {
              label: 'Specialized Care',
              color: 'bg-amber-50 text-amber-700 border-amber-200'
            }].map((cat, idx) => <div key={idx} className={`rounded-xl p-4 border text-center font-semibold text-sm ${cat.color}`} data-id="element-670">
                  <CheckCircleIcon className="h-5 w-5 mx-auto mb-2" data-id="element-671" />
                  {cat.label}
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20 bg-light-alt" data-id="element-672">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-673">
          <div ref={servicesRef} className={`transition-all duration-1000 ease-out ${isServicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-674">
            <div className="text-center mb-14" data-id="element-675">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-676">
                Our In-Home Services
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-677"></div>
              <p className="text-lg text-dark-muted" data-id="element-678">
                Comprehensive care tailored to each client's unique needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-id="element-679">
              {services.map((service, idx) => <div key={idx} className="bg-white rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-100 transition-all duration-300 transform hover:-translate-y-1 group flex items-start" data-id="element-680">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 mr-5 group-hover:bg-primary transition-colors duration-300" data-id="element-681">
                    <service.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-682" />
                  </div>
                  <div data-id="element-683">
                    <h3 className="text-lg font-bold text-dark mb-2" data-id="element-684">
                      {service.title}
                    </h3>
                    <p className="text-dark-muted text-sm leading-relaxed" data-id="element-685">
                      {service.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white" data-id="element-686">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-687">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-688">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-689"></div>
            <div className="relative z-10" data-id="element-690">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-691">
                Let Us Care for Your Loved One
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-692">
                Contact us today to discuss a personalized in-home care plan.
                We'll match your loved one with the right caregiver for their
                unique needs.
              </p>
              <a href="#/contact" className="inline-flex items-center bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 group" data-id="element-693">
                Contact Us Today
                <ArrowRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-694" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-695" />
    </main>;
}